/**
 * SYSTEM INTEGRITY — whether the systems a Role holds can WORK AT ALL, as one
 * org-scoped read.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * WHY THIS IS NOT `OrgRolesRunHealthViewSchema`
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Run health asks **did it FAIL when it ran**. This asks **can it WORK at
 * all**. They are different claims from different sources with different
 * populations, and the seam is not a preference — run health STRUCTURALLY
 * cannot answer this one:
 *
 * | | run health | integrity |
 * |---|---|---|
 * | asks | did it fail when it ran | can it work at all |
 * | kind | retrospective OUTCOME | prospective INTEGRITY |
 * | population | `workflow` only — the sole kind recording an outcome | all five `RoleResourceType` members |
 * | source | `WorkflowExecution`, over a window | columns on the held row itself |
 * | storage | none, recomputed per request | one row per held system |
 * | answers *since when* | no | **yes — that is why it stores** |
 *
 * 🔴 A `RoleResource` pointing at a DELETED workflow reads as `idle` in run
 * health, byte-identical in the payload to a live workflow nobody ran. Its
 * `idle` bucket is where every broken-but-unrun system already goes, and that
 * schema states *"IDLE IS NOT HEALTHY AND IT IS NOT BROKEN"* — a correct
 * refusal, because it cannot tell those apart. This can.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * 🚨 THE VOCABULARY IS INTEGRITY / REPAIR, NEVER HEALTH
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Two things called "health" in one table cell is the "two numbers, one word"
 * defect: `SystemHealthStrip` already draws a per-system mark from run health
 * in the register's `SYSTEMS · STATE OF EACH` cell, keyed on the same
 * `RoleResource.resourceId` this schema keys on. `NEEDS_REPAIR` is a noun a
 * human acts on and can never be read as run health's `failing`.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * 🔴 THREE VERDICTS, AND `UNKNOWN` IS NOT A SHADE OF EITHER OTHER ONE
 * ══════════════════════════════════════════════════════════════════════════
 *
 * A false RED is reported within a day. A false GREEN never is. So a probe that
 * ERRORS resolves to `UNKNOWN` — never to `INTACT`, which would be the false
 * green nobody ever finds. Collapsing `UNKNOWN` into `NEEDS_REPAIR` is the
 * wrong cure in the other direction: a probe outage would then read as a
 * customer incident, a false RED at scale, and a model that cries wolf is
 * switched off inside a week.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * 🚨 THE CLIENT MAY NEVER INVENT A DENOMINATOR
 * ══════════════════════════════════════════════════════════════════════════
 *
 * `systemsUnknown` and `systemsExcluded` are served as their own fields, the
 * same rule `systemsObservable` / `systemsUnobservable` keeps for run health.
 * An organisation whose probes all failed renders `0 intact, 0 needs-repair,
 * N unknown` — never clean. And an EXCLUDED system is not an unknown one: see
 * the lifecycle note below.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * ⚠️ COVERAGE OF CHANNEL CREDENTIALS IS PARTIAL, AND SAYING SO IS THE POINT
 * ══════════════════════════════════════════════════════════════════════════
 *
 * `INTACT` on a deployment means *intact on every predicate this model can
 * evaluate from the database*. No probe here makes an outbound call, which is
 * what keeps a provider outage from becoming a customer-facing red — but it
 * has a declared ceiling. OAuth credential validity is read as
 * `OAuthConnection.status`, maintained by the existing `OAUTH_TOKEN_REFRESH`
 * cron on a MONTHLY default schedule, so a latch can be up to 30 days stale.
 * The API-key half leaves no database trace at all: `ApiKeyConnection.status`
 * has no production writer on a send failure, so a revoked Twilio auth token, a
 * revoked Meta page token or a deleted Telegram bot is UNDETECTABLE from here
 * and the row stays `CONNECTED` forever. Detecting that class needs one
 * outbound call per connection and is a different cost class, a different
 * failure mode and a different lane.
 */

import { z } from "zod";

import { RoleResourceTypeSchema } from "../roles/role.types";

/**
 * ══════════════════════════════════════════════════════════════════════════
 * THE VERDICT
 * ══════════════════════════════════════════════════════════════════════════
 *
 * - `INTACT` — every predicate ran and none fired.
 * - `NEEDS_REPAIR` — a predicate FIRED. A work item with an age, never a fix:
 *   every predicate in this model fires on a CUSTOMER-OWNED artifact, so there
 *   is no auto-repair to design. The server cannot publish someone's workflow,
 *   resume their agent, upload their missing `.docx` or re-authorise their
 *   Google account.
 * - `UNKNOWN` — a probe could not be evaluated. Not green, not red, its own
 *   bucket.
 */
export const ROLE_SYSTEM_INTEGRITY_VERDICTS = ["INTACT", "NEEDS_REPAIR", "UNKNOWN"] as const;

export const RoleSystemIntegrityVerdictSchema = z.enum(ROLE_SYSTEM_INTEGRITY_VERDICTS);

export type RoleSystemIntegrityVerdict = (typeof ROLE_SYSTEM_INTEGRITY_VERDICTS)[number];

/**
 * WHY a system is not `INTACT` — a CLOSED vocabulary, never free text.
 *
 * 🔴 EVERY MEMBER NAMES A PREDICATE WITH A CODE PATH THAT FAILS WHEN IT FIRES.
 * That is half of this model's admission test — the other half is a production
 * distribution that discriminates — and a reason with no failing path behind it
 * ships a constant wearing a signal. Each member below names the throw it
 * corresponds to, so the two can be checked against each other by reading.
 *
 * Two members are `UNKNOWN` reasons rather than `NEEDS_REPAIR` ones and are
 * grouped at the end.
 */
export const ROLE_SYSTEM_INTEGRITY_REASONS = [
  /**
   * Probe 0, and the only reason that applies to all five kinds: the held
   * `resourceId` no longer resolves to a live row of that type in that
   * organisation, under exactly the predicate the attach path uses at mint.
   *
   * `RoleResource.resourceId` has NO foreign key and the table has exactly two
   * writers — the attach upsert and the explicit unassign — so no delete of an
   * Agent, Workflow, Deployment, AITask or DocumentTemplate removes its row,
   * and no cascade can, because there is nothing to travel. Three read paths
   * already document the residue as accepted in their own headers
   * (`HeldResourceNamesService`, `roleDeletionInventory`,
   * `UnassignResourceUseCase`). Nothing detects it today.
   */
  "target_missing",

  /** `Agent.status = PAUSED`. `PrismaExecutionGuardAdapter.isAgentPaused` refuses every execution with `ExecutionBlockedError`. */
  "agent_paused",

  /** `Workflow.status ∈ {DRAFT, ARCHIVED, PAUSED}` or `archivedAt IS NOT NULL`. `WorkflowExecutionService` throws *"…cannot be triggered by agents. Please ask the administrator to publish it first."* */
  "workflow_not_published",

  /** `Workflow.status = PUBLISHED` with an empty `publishedNodes`. Same service throws *"Published workflow data not found"*; `ScheduleJobProcessor` logs *"No published nodes found"* and returns without running the tick. */
  "workflow_published_without_nodes",

  /** `Deployment.isActive = false`. `StreamChatMessageUseCase` throws `ForbiddenException("Deployment is not active")`. */
  "deployment_inactive",

  /** The deployment's `type` requires a connection (`getRequiredConnectionType`) and the matching connection id is NULL. Nothing can authenticate the channel. */
  "deployment_connection_missing",

  /** `OAuthConnection.status ∈ {NEEDS_REAUTH, DISCONNECTED}`. `OAuthConnectionProvider.getConnection` refuses before it ever reaches the provider. */
  "deployment_connection_needs_reauth",

  /**
   * `OAuthConnection.service` is not the service this `DeploymentType`
   * expects (`getExpectedOAuthService`). Its own arm in
   * `OAuthConnectionProvider` refuses BEFORE the status checks, deliberately:
   * re-authenticating cannot fix a wrong-provider connection, so latching
   * `NEEDS_REAUTH` would send the customer a remedy that cannot work.
   */
  "deployment_connection_wrong_service",

  /**
   * `Deployment.agentId IS NULL`, or it names an Agent that is soft-deleted.
   * A soft-delete hole rather than a redundant FK check: `agentId` is
   * `onDelete: SetNull`, which fires on a HARD delete only, while the soft
   * delete path sets `deletedAt` and touches no deployment. The read side then
   * resolves `agent: null` and the channel has nothing to run.
   */
  "deployment_agent_missing",

  /**
   * `AITask.outputFormat = TEMPLATE` with no usable template — either
   * `documentTemplateId IS NULL`, or the template it names has no `fileUrl`.
   *
   * 🔴 BOTH ARMS ARE ONE `if`, WHICH IS WHY THEY ARE ONE REASON.
   * `TemplateProducer.produce` refuses on `!task.documentTemplate?.fileUrl` and
   * throws *"Document template is required for template output format"* —
   * optional-chaining across exactly these two states. A probe checking only
   * the id checks half of a two-clause runtime guard.
   *
   * The residue is produced by the console: `documentTemplateId` is an OPTIONAL
   * relation, so deleting a template SET NULLs it, and the delete does not
   * refuse when dependents exist. The task survives with `outputFormat` still
   * TEMPLATE and nothing anywhere reports it.
   */
  "ai_task_template_output_without_template",

  /** `DocumentTemplate.fileUrl IS NULL`. `DocumentTemplateGenerationService.generateFromTemplate` throws *"Template file URL not found"* on all three of its type arms, covering every `DocumentTemplateType` member. */
  "document_template_file_missing",

  /**
   * ── `UNKNOWN` reasons from here down ──────────────────────────────────────
   *
   * The row's stored `resourceType` is not a current `RoleResourceType`.
   * That column is loose TEXT with no database enum, so it can hold a RETIRED
   * member: `collection` and `external_tool` were both in this union once, and
   * the old rows did not disappear when it narrowed. Indexing an exhaustive
   * `Record<RoleResourceType, Port>` with such a value yields `undefined` and
   * throws `TypeError` on `.execute` — a 500 for the whole band on the read, or
   * a dead tick for that tenant on the sweep.
   */
  "retired_resource_type",

  /** The probe threw or its read failed. Its own bucket precisely so a probe outage can never be mistaken for a clean estate. */
  "probe_failed"
] as const;

export const RoleSystemIntegrityReasonSchema = z.enum(ROLE_SYSTEM_INTEGRITY_REASONS);

export type RoleSystemIntegrityReason = (typeof ROLE_SYSTEM_INTEGRITY_REASONS)[number];

/**
 * One held system's verdict.
 *
 * `systemId` is `RoleResource.resourceId`, which is what `SystemHealthStrip`
 * already keys its marks on — so this payload joins to the marks already on
 * screen rather than introducing a second identity for the same thing.
 */
export const RoleProbedSystemSchema = z.strictObject({
  systemId: z.string(),

  /**
   * NULL exactly when `reason === "retired_resource_type"`, and never otherwise.
   *
   * 🔴 THE NULL IS WHY THE ROW IS STILL HERE AT ALL. `RoleResource.resourceType`
   * is loose TEXT and can hold a member this build's union no longer has, so
   * there is genuinely no `RoleResourceType` to publish for such a row. Dropping
   * it from the payload instead would be the cheaper shape and is wrong twice
   * over: it would understate `systemsProbed`, which is the one field stopping a
   * client from inventing a denominator, and it would leave
   * `retired_resource_type` a reason with no producer — an enum member nothing
   * can emit, which is indistinguishable from a bug.
   *
   * A client renders such a system as unclassifiable, never as a mark of some
   * assumed kind.
   */
  resourceType: RoleResourceTypeSchema.nullable(),

  verdict: RoleSystemIntegrityVerdictSchema,

  /**
   * NULL exactly when `verdict === "INTACT"`, and non-null otherwise — the same
   * exclusive-or `RoleSystemIntegrity_reason_xor_intact` enforces, so the wire
   * and the column cannot drift into disagreeing.
   */
  reason: RoleSystemIntegrityReasonSchema.nullable(),

  /**
   * When the SWEEP first saw this system broken in its current unbroken run of
   * `NEEDS_REPAIR`, ISO-8601. NULL on every other verdict, and NULL on a
   * `NEEDS_REPAIR` system the sweep has not reached yet.
   *
   * 🔴 IT ANSWERS *"THE SWEEP FIRST SAW THIS BROKEN AT T"*, NEVER *"IT BROKE AT
   * T"*. The two differ by at most one sweep interval and the distinction is
   * load-bearing: nothing in this product records the instant a workflow was
   * unpublished, so the honest claim is the observation, not the event. This is
   * also the whole reason the model STORES rather than only computing — a past
   * day is unrecoverable by any query, so capturing it nightly is the only
   * mechanism there is.
   */
  brokenSince: z.string().datetime({ offset: true }).nullable()
});

export type RoleProbedSystem = z.infer<typeof RoleProbedSystemSchema>;

/**
 * 🚨 THE THREE VERDICT BUCKETS PARTITION `systemsProbed` EXACTLY:
 * `systemsIntact + systemsNeedsRepair + systemsUnknown === systemsProbed`,
 * always, and `probedSystems.length === systemsProbed` with the histogram equal
 * term for term. A client may therefore draw the ratio it is handed and must
 * never compute a denominator of its own.
 *
 * `systemsExcluded` sits OUTSIDE that identity on purpose — see its own field.
 */
export const RoleSystemIntegrityRowSchema = z.strictObject({
  roleId: z.string(),
  roleName: z.string(),

  systemsProbed: z.number().int().min(0),
  systemsIntact: z.number().int().min(0),
  systemsNeedsRepair: z.number().int().min(0),
  systemsUnknown: z.number().int().min(0),

  /**
   * Held systems deliberately kept OUT of the probed population because their
   * `RoleResource.lifecycle` is `BUILDING` or `RETIRED`.
   *
   * 🔴 EXCLUDED IS NOT UNKNOWN, AND FOLDING THE TWO WOULD BREAK THE ONE FIELD
   * THAT STOPS AN INVENTED DENOMINATOR. `UNKNOWN` means *a probe could not
   * answer* — a real gap in coverage. A system nobody asked us to probe is not
   * a gap. Only `LIVE` asserts *this is supposed to be working*, so only `LIVE`
   * can carry a `NEEDS_REPAIR`: reporting a half-built workflow as broken is
   * correct on the predicate, useless as a signal, and exactly how a model gets
   * switched off.
   *
   * ⚠️ The column is `NOT NULL DEFAULT 'LIVE'`, so its migration enrols every
   * existing row on the day it lands and it can never grandfather anything.
   */
  systemsExcluded: z.number().int().min(0),

  /**
   * One entry per PROBED system — never per held system. A Role holding
   * nothing, or holding only `BUILDING` systems, gets a row with an empty array
   * and `systemsProbed: 0`, which is a different statement from "everything is
   * intact" and has its own fields to say so.
   */
  probedSystems: z.array(RoleProbedSystemSchema)
});

export type RoleSystemIntegrityRow = z.infer<typeof RoleSystemIntegrityRowSchema>;

/** The same four buckets plus the exclusion count, across every Role in the organisation. */
export const OrgRolesSystemIntegrityTotalsSchema = z.strictObject({
  roles: z.number().int().min(0),
  systemsProbed: z.number().int().min(0),
  systemsIntact: z.number().int().min(0),
  systemsNeedsRepair: z.number().int().min(0),
  systemsUnknown: z.number().int().min(0),
  systemsExcluded: z.number().int().min(0)
});

export type OrgRolesSystemIntegrityTotals = z.infer<typeof OrgRolesSystemIntegrityTotalsSchema>;

export const OrgRolesSystemIntegrityViewSchema = z.strictObject({
  organizationId: z.string(),
  totals: OrgRolesSystemIntegrityTotalsSchema,
  roles: z.array(RoleSystemIntegrityRowSchema)
});

export type OrgRolesSystemIntegrityView = z.infer<typeof OrgRolesSystemIntegrityViewSchema>;
